import { Track } from 'livekit-client';

/**
 * Minimal surface of a LiveKit media track the UI needs: attach it to a DOM element, detach it
 * again. livekit-client's Track satisfies this structurally, and tests substitute plain fakes.
 */
export interface AttachableTrack {
  attach(element: HTMLMediaElement): HTMLMediaElement;
  detach(element?: HTMLMediaElement): HTMLMediaElement[];
}

/** Structural slice of livekit-client's TrackPublication used for tile derivation. */
export interface TilePublication {
  readonly kind: string;
  readonly source: string;
  /** True while the publication is muted (the publisher toggled the device off). */
  readonly isMuted: boolean;
  readonly track?: AttachableTrack | undefined;
}

/**
 * Structural slice of livekit-client's RemoteTrackPublication that the tile subscription
 * policy needs (subscribeQuality.ts): request the desired dimensions for this track.
 * livekit-client takes the SMALLER of these requested dimensions and the adaptive-stream
 * element dimensions, so a request acts as a ceiling without breaking graceful shrink.
 * Present on REMOTE publications only (LocalTrackPublication has no such method) - local
 * tiles are not subscribed, so their tiles carry null.
 */
export interface TileSubscriptionControl {
  setVideoDimensions(dimensions: { readonly width: number; readonly height: number }): void;
  /**
   * Explicit subscribe/unsubscribe (livekit-client's RemoteTrackPublication.setSubscribed),
   * the large-room pagination's off-switch (gridLimits.ts): an off-page publication is
   * unsubscribed because a subscribed track without an attached element would keep decoding -
   * adaptiveStream's visibility pause only reaches elements that exist. Optional in the type
   * so fakes that only model the dimensions ladder stay valid; the live SDK publication
   * always carries it, and the layout guards on presence.
   */
  setSubscribed?(subscribed: boolean): void;
}

/** Structural slice of livekit-client's Participant; keeps the derivation pure and testable. */
export interface TileParticipant {
  readonly identity: string;
  readonly name?: string | undefined;
  /** Getter property on livekit-client's Participant (true while the microphone sends audio). */
  readonly isMicrophoneEnabled: boolean;
  readonly trackPublications: ReadonlyMap<string, TilePublication>;
}

/** One rendered participant tile, derived from the room on every state change. */
export interface ParticipantTile {
  readonly identity: string;
  readonly label: string;
  readonly isLocal: boolean;
  readonly micMuted: boolean;
  readonly videoTrack: AttachableTrack | null;
  /**
   * The camera publication's subscription control (remote camera publications only): the
   * meeting layout requests the tile's desired dimensions through it. Null for local
   * tiles (never subscribed) and for camera publications without a track.
   */
  readonly videoSubscription: TileSubscriptionControl | null;
}

/**
 * One screen share being viewed: a participant's screen video track rendered as its own
 * large tile, separate from the face tiles. livekit-client publishes at most one
 * ScreenShare video track per participant (one publication per source), so the pair
 * (participant identity) identifies the share.
 */
export interface ScreenShareTile {
  readonly identity: string;
  readonly label: string;
  readonly isLocal: boolean;
  readonly track: AttachableTrack;
}

/** One remote audio track that needs a hidden playback element. */
export interface RemoteAudioTrack {
  readonly identity: string;
  readonly track: AttachableTrack;
}

/**
 * The three connection states the room UI surfaces, mapped from LiveKit room events.
 * These exact strings feed the conn-state test id (e2e asserts `connected` after join).
 */
export type ConnectionPhase = 'connected' | 'reconnecting' | 'disconnected';

/**
 * DisconnectReason.CLIENT_INITIATED from @livekit/protocol (protobuf enum value 1): the value
 * livekit-client 2.22.3 passes to RoomEvent.Disconnected after a local room.disconnect(). Kept
 * as a literal so this module stays independent of the SDK import that tests mock.
 */
const CLIENT_INITIATED_DISCONNECT = 1;

/**
 * Stable kinds for the user-facing disconnect reasons (room.drop_reason_* in the i18n catalog):
 * the lib layer maps raw LiveKit DisconnectReason numbers onto kinds, the UI translates them.
 */
export type DisconnectReasonKind =
  | 'lost'
  | 'identity_conflict'
  | 'server_shutdown'
  | 'removed'
  | 'room_deleted'
  | 'session_lost'
  | 'join_failed'
  | 'signalling_closed'
  | 'room_closed'
  | 'timed_out';

/** DisconnectReason values from @livekit/protocol mapped onto stable UI kinds. */
const DISCONNECT_REASON_KIND: Readonly<Record<number, DisconnectReasonKind>> = {
  0: 'lost',
  2: 'identity_conflict',
  3: 'server_shutdown',
  4: 'removed',
  5: 'room_deleted',
  6: 'session_lost',
  7: 'join_failed',
  9: 'signalling_closed',
  10: 'room_closed',
  14: 'timed_out',
};

/** Room slug rule from docs/contract.md: ^[a-z0-9][a-z0-9-]{2,63}$ (3-64 chars). */
const ROOM_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{2,63}$/;

/** Matches /r/:room paths (optional trailing slash); anything else is not a room page. */
const ROOM_PATH_PATTERN = /^\/r\/([^/]+)\/?$/;

const CONTROL_CHARACTER_PATTERN = /\p{Cc}/u;

/** True when the value is a valid room slug per the frozen contract. */
export function isValidRoomSlug(value: string): boolean {
  return ROOM_SLUG_PATTERN.test(value);
}

/** Extracts the room segment of a /r/:room path, or null for any other path. Raw, unvalidated. */
export function roomSlugFromPath(pathname: string): string | null {
  const match = pathname.match(ROOM_PATH_PATTERN);
  if (match === null) {
    return null;
  }
  return match[1] ?? null;
}

/**
 * Stable kinds of display-name validation errors. The lib layer stays language-independent:
 * the UI translates the kind through the i18n catalog (prejoin.name_error_*).
 */
export type DisplayNameError = 'empty' | 'too_long' | 'control_chars';

/** Outcome of display-name validation: the trimmed value plus a stable error kind, if any. */
export interface DisplayNameCheck {
  readonly value: string;
  readonly error: DisplayNameError | null;
}

/** Trims and validates a display name against the join contract (at most 64 chars, no control chars). */
export function checkDisplayName(input: string): DisplayNameCheck {
  const value = input.trim();
  if (value.length === 0) {
    return { value, error: 'empty' };
  }
  if (value.length > 64) {
    return { value, error: 'too_long' };
  }
  if (CONTROL_CHARACTER_PATTERN.test(value)) {
    return { value, error: 'control_chars' };
  }
  return { value, error: null };
}

function labelOf(participant: TileParticipant): string {
  return participant.name !== undefined && participant.name.length > 0
    ? participant.name
    : participant.identity;
}

function toTile(participant: TileParticipant, isLocal: boolean): ParticipantTile {
  const camera = cameraSurface(participant);
  return {
    identity: participant.identity,
    label: labelOf(participant),
    isLocal,
    micMuted: !participant.isMicrophoneEnabled,
    videoTrack: camera?.track ?? null,
    videoSubscription: isLocal ? null : (camera?.subscription ?? null),
  };
}

/** The camera publication's track plus its subscription control, or null when absent. */
interface CameraSurface {
  readonly track: AttachableTrack;
  readonly subscription: TileSubscriptionControl | null;
}

/**
 * The participant's camera video track: the first video publication that is not a
 * screen share, carries a track (unsubscribed publications have none) and is not muted.
 * A participant sharing their screen has two video tracks; the face tile must keep
 * showing the face. A MUTED camera publication reads as camera-off (the avatar shows):
 * the toggle keeps the track published on purpose (mute is reversible), but every
 * viewer's tile must reflect the off state - locally and remotely (TrackMuted flips
 * the remote publication's isMuted, and the mute re-render reaches this derivation).
 * Remote publications also expose the subscription control when the live SDK object
 * carries the setVideoDimensions method (a structural check keeps fakes honest).
 */
function cameraSurface(participant: TileParticipant): CameraSurface | null {
  for (const publication of participant.trackPublications.values()) {
    if (
      publication.kind === Track.Kind.Video &&
      publication.source !== Track.Source.ScreenShare &&
      publication.track !== undefined &&
      !publication.isMuted
    ) {
      return { track: publication.track, subscription: subscriptionControlOf(publication) };
    }
  }
  return null;
}

/**
 * The publication's subscription control, or null when the object does not carry the
 * setVideoDimensions method (local publications, test fakes, future SDK shapes).
 */
function subscriptionControlOf(publication: TilePublication): TileSubscriptionControl | null {
  // The single documented structural cast (cf. publishQuality.ts's scalabilityModesOf):
  // livekit-client's RemoteTrackPublication carries setVideoDimensions while other shapes
  // (LocalTrackPublication, test fakes) do not - the runtime check decides.
  const candidate = publication as { setVideoDimensions?: unknown };
  if (typeof candidate.setVideoDimensions !== 'function') {
    return null;
  }
  return candidate as TileSubscriptionControl;
}

/**
 * The participant's screen-share video track, or null when they are not sharing
 * (or the share is not subscribed yet — publications without a track are skipped).
 */
function screenShareTrack(participant: TileParticipant): AttachableTrack | null {
  for (const publication of participant.trackPublications.values()) {
    if (
      publication.kind === Track.Kind.Video &&
      publication.source === Track.Source.ScreenShare &&
      publication.track !== undefined
    ) {
      return publication.track;
    }
  }
  return null;
}

/**
 * True while the participant has a live screen-share publication. Drives the local
 * share state (idle/sharing) off local publications, so the SDK unpublishing the
 * track (browser's stop bar ends it) flips the UI back without extra listeners.
 */
export function isScreenSharing(participant: TileParticipant | null): boolean {
  return participant !== null && screenShareTrack(participant) !== null;
}

/**
 * Derives the participant tiles from room state: one tile per participant, the local participant
 * first, remotes in stable identity order. The local tile wins on an identity collision.
 */
export function deriveTiles(
  local: TileParticipant | null,
  remotes: ReadonlyMap<string, TileParticipant>,
): ParticipantTile[] {
  const tiles = new Map<string, ParticipantTile>();
  for (const participant of remotes.values()) {
    tiles.set(participant.identity, toTile(participant, false));
  }
  if (local !== null) {
    tiles.set(local.identity, toTile(local, true));
  }
  return [...tiles.values()].sort((a, b) => {
    if (a.isLocal !== b.isLocal) {
      return a.isLocal ? -1 : 1;
    }
    return a.identity.localeCompare(b.identity);
  });
}

/**
 * Derives the screen-share tiles from room state: one tile per participant that is
 * currently sharing, the local participant's self-view first, remotes in stable
 * identity order. Several participants may share simultaneously; all of them render.
 */
export function deriveScreenShares(
  local: TileParticipant | null,
  remotes: ReadonlyMap<string, TileParticipant>,
): readonly ScreenShareTile[] {
  const shares: ScreenShareTile[] = [];
  for (const participant of remotes.values()) {
    const track = screenShareTrack(participant);
    if (track !== null) {
      shares.push({
        identity: participant.identity,
        label: labelOf(participant),
        isLocal: false,
        track,
      });
    }
  }
  if (local !== null) {
    const track = screenShareTrack(local);
    if (track !== null) {
      shares.push({ identity: local.identity, label: labelOf(local), isLocal: true, track });
    }
  }
  return shares.sort((a, b) => {
    if (a.isLocal !== b.isLocal) {
      return a.isLocal ? -1 : 1;
    }
    return a.identity.localeCompare(b.identity);
  });
}

/** Collects every subscribed remote audio track that needs a playback element. */
export function remoteAudioTracks(
  remotes: ReadonlyMap<string, TileParticipant>,
): readonly RemoteAudioTrack[] {
  const tracks: RemoteAudioTrack[] = [];
  for (const participant of remotes.values()) {
    for (const publication of participant.trackPublications.values()) {
      if (publication.kind === Track.Kind.Audio && publication.track !== undefined) {
        tracks.push({ identity: participant.identity, track: publication.track });
      }
    }
  }
  return tracks;
}

/**
 * Maps the local participant's LiveKit connection quality onto the SignalBars level of the
 * top-bar chip: excellent -> 3, good -> 2, poor/lost -> 1. Anything else (the 'unknown'
 * state before the first quality report) shows the full three bars.
 */
export function signalLevel(quality: string): 1 | 2 | 3 {
  if (quality === 'poor' || quality === 'lost') {
    return 1;
  }
  if (quality === 'good') {
    return 2;
  }
  return 3;
}

/**
 * The right-hand side panel: participants list, chat, or the recordings history (the
 * recordings tab renders for moderators/admins only).
 */
export type PanelMode = 'chat' | 'participants' | 'recordings';

/**
 * Side-panel toggle: picking the open panel's target closes it, any other target opens (or
 * switches) it. A null current means the panel is closed and the target opens it.
 */
export function nextPanel(current: PanelMode | null, target: PanelMode): PanelMode | null {
  return current === target ? null : target;
}

/**
 * Formats a meeting timer from whole elapsed seconds: `MM:SS` under an hour, `H:MM:SS` from
 * one hour up. Negative inputs clamp to zero (clock skew between tabs must not crash the bar).
 */
export function formatMeetingTimer(totalSeconds: number): string {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const seconds = safeSeconds % 60;
  const minutes = Math.floor(safeSeconds / 60) % 60;
  const hours = Math.floor(safeSeconds / 3600);
  const twoDigits = (value: number): string => value.toString().padStart(2, '0');
  const mmss = `${twoDigits(minutes)}:${twoDigits(seconds)}`;
  return hours > 0 ? `${hours}:${mmss}` : mmss;
}

/** Pin toggle: pinning a new participant replaces the pin, pinning the pinned one clears it. */
export function togglePinned(pinned: string | null, identity: string): string | null {
  return pinned === identity ? null : identity;
}

/**
 * Identities of the currently rendered tiles that are speaking. LiveKit orders the
 * RoomEvent.ActiveSpeakersChanged payload loudest-first (local participant included), so the
 * raw list is already ranked; this only intersects it with the rendered tiles so the ring
 * never highlights a participant who already left.
 */
export function speakingIdentities(
  tiles: readonly ParticipantTile[],
  activeSpeakers: readonly string[],
): ReadonlySet<string> {
  const rendered = new Set(tiles.map((tile) => tile.identity));
  const speaking = new Set<string>();
  for (const identity of activeSpeakers) {
    if (rendered.has(identity)) {
      speaking.add(identity);
    }
  }
  return speaking;
}

/**
 * Maps the room's ConnectionState to the connection states the UI shows. The SDK's
 * 'connecting' and 'signalReconnecting' intermediates get no dedicated UI: connecting only
 * happens before the first join completes (prejoin/joining screens), and a signal-only
 * reconnect is usually invisible to the user, so both clamp to the nearest surface.
 */
export function connectionPhaseFromRoomState(state: string): ConnectionPhase {
  if (state === 'connected') {
    return 'connected';
  }
  if (state === 'reconnecting' || state === 'signalReconnecting') {
    return 'reconnecting';
  }
  return 'disconnected';
}

/**
 * True when the RoomEvent.Disconnected reason says the local client asked to leave
 * (Leave button, pagehide, teardown) rather than the connection failing.
 */
export function isUserInitiatedDisconnect(reason: unknown): boolean {
  return reason === CLIENT_INITIATED_DISCONNECT;
}

/** Stable kind for a DisconnectReason value; unknown or absent reasons stay generic ('lost'). */
export function disconnectReasonKind(reason: unknown): DisconnectReasonKind {
  if (typeof reason === 'number') {
    return DISCONNECT_REASON_KIND[reason] ?? 'lost';
  }
  return 'lost';
}
